/**
 * StorefrontHome — Nuevo home enriquecido de la tienda pública.
 *
 * Diseño basado en mockup del cliente:
 *   • Header: logo, buscador, ayuda, mi cuenta, carrito
 *   • Nav azul: "Todas las categorías" + tabs + pill "Ofertas"
 *   • Hero banner (carrusel con dots)
 *   • Compra por categoría (6 tarjetas visuales)
 *   • ¿Para qué necesitas tu equipo? (use_cases)
 *   • Ofertas destacadas (carrusel horizontal de productos)
 *   • Compra por presupuesto (4 rangos de precio)
 *   • CTA WhatsApp
 *   • Trust footer + footer completo
 *
 * Sólo muestra productos con `show_in_storefront=true`.
 * Todos los filtros llevan a `/catalogo` con querystring aplicado.
 * Ruta: `/tienda`
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MagnifyingGlass, ShoppingCartSimple, User, Question, Plus,
  Laptop, Desktop, Monitor, DeviceMobile, GameController, Headphones,
  ChatCircleText, Truck, ShieldCheck, CreditCard, Headset, WhatsappLogo,
  CaretLeft, CaretRight, Tag, GraduationCap, Briefcase, House, PaintBrush, Coins,
  Cpu,
} from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import HomeProductCard from "@/components/storefront/HomeProductCard";
import HomeCatalog, { useHomeCatalog, scrollToCatalog } from "@/components/storefront/HomeCatalog";
import QuickViewModal from "@/components/storefront/QuickViewModal";
import { CartDrawer } from "@/components/storefront/CartDrawer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  Laptops: Laptop, Computadoras: Desktop, Monitores: Monitor,
  Celulares: DeviceMobile, Gaming: GameController, Accesorios: Headphones,
};

const USE_CASE_ICONS = {
  escuela: GraduationCap, trabajo: Briefcase, hogar: House,
  gaming: GameController, diseno: PaintBrush, economica: Coins,
};

// Hero slides de respaldo cuando aún no hay banners configurados desde el admin.
// Si `data.banners.hero` trae elementos, se usan esos.
const FALLBACK_HERO = [
  {
    id: "_fb1",
    title: "El equipo ideal para tu productividad",
    subtitle: "Laptops, computadoras y monitores con envío express.",
    cta_label: "Ver ofertas",
    cta_href: "/catalogo",
    accent_color: "#2563eb",
    image_url: "",
  },
  {
    id: "_fb2",
    title: "Gaming sin compromisos",
    subtitle: "Equipos armados con las mejores tarjetas gráficas y procesadores.",
    cta_label: "Ver gaming",
    cta_href: "/catalogo?category=Gaming",
    accent_color: "#7C3AED",
    image_url: "",
  },
  {
    id: "_fb3",
    title: "Accesorios que marcan la diferencia",
    subtitle: "Audífonos, teclados y todo lo que necesita tu setup.",
    cta_label: "Explorar accesorios",
    cta_href: "/catalogo?category=Accesorios",
    accent_color: "#059669",
    image_url: "",
  },
];


export default function StorefrontHome() {
  const [data, setData] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [query, setQuery] = useState("");
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("wl_v1") || "[]"); } catch { return []; }
  });
  const { count: itemCount, addItem, setDrawerOpen } = useCart();
  const offersRef = useRef(null);
  const catalog = useHomeCatalog();
  const [quick, setQuick] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/storefront/home`)
      .then((r) => setData(r.data))
      .catch(() => setData({ company: { name: "Tienda" }, categories: [], use_cases: [], offers: [], featured: [], budgets: [], banners: { hero: [], promo: [] } }));
  }, []);

  // Hero slides efectivos: usa los administrados por admin si existen, sino fallback.
  const heroSlides = useMemo(() => {
    const admin = data?.banners?.hero || [];
    return admin.length > 0 ? admin : FALLBACK_HERO;
  }, [data]);

  const promoBanners = useMemo(() => data?.banners?.promo || [], [data]);

  useEffect(() => {
    if (heroSlides.length <= 1) return undefined;
    const id = setInterval(() => setSlideIdx((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(id);
  }, [heroSlides.length]);

  // Si cambia el número de slides mientras estamos en un índice fuera de rango, corrige.
  useEffect(() => {
    if (slideIdx >= heroSlides.length) setSlideIdx(0);
  }, [heroSlides.length, slideIdx]);

  const toggleWish = (id) => {
    setWishlist((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem("wl_v1", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const addToCart = (p) => {
    addItem({ id: p.id, name: p.name, brand: p.brand, photos: p.photos, store_price: p.store_price, sale_currency: p.sale_currency, max_qty: p.max_qty }, 1);
    toast.success("Agregado al carrito");
  };

  // Como en el mockup: la búsqueda filtra el catálogo del home en vivo; Enter baja al catálogo.
  const doSearch = (e) => {
    e?.preventDefault?.();
    scrollToCatalog();
  };
  const onQuery = (q) => { setQuery(q); catalog.setQuery(q); };
  const addFromQuickView = (p) => { addToCart(p); setQuick(null); setDrawerOpen(true); };
  const budgetList = useMemo(() => (data?.budgets || []).map((b) => ({ key: b.key, label: b.label, min: b.min, max: b.max })), [data]);
  const needLabels = useMemo(() => Object.fromEntries((data?.use_cases || []).map((u) => [u.key, u.label])), [data]);

  const company = data?.company || { name: "Tienda" };
  const whatsapp = (company.phone || "").replace(/\D/g, "");
  const whatsappHref = whatsapp
    ? `https://wa.me/${whatsapp}?text=${encodeURIComponent("Hola, me interesa un equipo de su catálogo.")}`
    : null;

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* ═════════════════ Header top ═════════════════ */}
      <header className="border-b border-zinc-100 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <Link to="/tienda" className="flex items-center gap-2 shrink-0" data-testid="home-logo">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="h-10 w-10 object-contain rounded" />
            ) : (
              <div className="h-10 w-10 rounded bg-[#002FA7] text-white font-heading font-bold flex items-center justify-center text-lg">A</div>
            )}
            <div className="hidden sm:block">
              <div className="font-heading font-extrabold text-lg leading-tight">{company.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">Tienda oficial</div>
            </div>
          </Link>
          <form onSubmit={doSearch} className="flex-1 max-w-2xl relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="Busca laptops, monitores, celulares, gaming…"
              className="w-full h-11 pl-10 pr-4 bg-zinc-50 border border-zinc-200 rounded-full text-sm focus:outline-none focus:border-[#002FA7] focus:bg-white transition-colors"
              data-testid="home-search"
            />
          </form>
          <div className="flex items-center gap-2">
            {whatsappHref && (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full text-sm hover:bg-zinc-50"
                data-testid="home-help"
              >
                <Question size={18} weight="regular" />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 leading-none">Ayuda</div>
                  <div className="text-xs font-semibold leading-tight">Escríbenos</div>
                </div>
              </a>
            )}
            <Link to="/portal" className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full text-sm hover:bg-zinc-50" data-testid="home-account">
              <User size={18} />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 leading-none">Cuenta</div>
                <div className="text-xs font-semibold leading-tight">Ingresar</div>
              </div>
            </Link>
            <button type="button" onClick={() => setDrawerOpen(true)} className="relative p-2 rounded-full hover:bg-zinc-50" data-testid="home-cart" aria-label="Abrir carrito">
              <ShoppingCartSimple size={22} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#E0000F] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ═════════════════ Nav azul ═════════════════ */}
        <nav className="bg-[#002FA7] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-11 flex items-center gap-6 text-sm">
            <button type="button" onClick={() => catalog.pickCategory("Todos")} className="hidden sm:inline-flex items-center gap-2 font-semibold hover:opacity-90" data-testid="nav-all-cats">
              <Plus size={14} weight="bold" /> Todas las categorías
            </button>
            {(data?.categories || []).slice(0, 5).map((c) => (
              <button
                type="button"
                key={c.key}
                onClick={() => catalog.pickCategory(c.key)}
                className={`hidden lg:inline-flex items-center font-medium transition-opacity ${catalog.state.category === c.key ? "opacity-100 underline underline-offset-4" : "opacity-90 hover:opacity-100"}`}
                data-testid={`nav-cat-${c.key}`}
              >
                {c.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-3">
              <button type="button" onClick={catalog.pickOffers} className="inline-flex items-center gap-1 bg-[#E0000F] hover:bg-[#c30414] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest" data-testid="nav-offers">
                <Tag size={12} weight="fill" /> Ofertas
              </button>
              <span className="hidden md:inline-flex items-center gap-1 text-[11px] opacity-90">
                <Truck size={13} weight="fill" /> Envíos a todo México
              </span>
            </div>
          </div>
        </nav>
      </header>

      {/* ═════════════════ Hero banner ═════════════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <div
          className="relative overflow-hidden rounded-2xl h-[280px] sm:h-[360px] flex items-center px-8 sm:px-14 text-white"
          style={{
            background: heroSlides[slideIdx]?.image_url
              ? `linear-gradient(135deg, ${heroSlides[slideIdx].accent_color || "#002FA7"}CC 0%, transparent 55%), url(${heroSlides[slideIdx].image_url}) center/cover`
              : `linear-gradient(135deg, ${heroSlides[slideIdx]?.accent_color || "#002FA7"} 0%, #001a5c 60%)`,
          }}
        >
          {/* Circular accents (solo visibles cuando no hay imagen de fondo) */}
          {!heroSlides[slideIdx]?.image_url && (
            <>
              <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-white/5" />
              <div className="absolute right-10 bottom-10 w-40 h-40 rounded-full bg-white/5" />
            </>
          )}
          <div className="relative z-10 max-w-xl">
            <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] opacity-80 mb-3">Nueva colección</div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
              {heroSlides[slideIdx]?.title || ""}
            </h1>
            {heroSlides[slideIdx]?.subtitle && (
              <p className="mt-3 text-sm sm:text-base opacity-90 max-w-md">
                {heroSlides[slideIdx].subtitle}
              </p>
            )}
            {heroSlides[slideIdx]?.cta_label && heroSlides[slideIdx]?.cta_href && (
              <HeroCta href={heroSlides[slideIdx].cta_href} label={heroSlides[slideIdx].cta_label} />
            )}
          </div>
          {heroSlides.length > 1 && (
            <div className="absolute right-6 bottom-6 flex gap-1.5">
              {heroSlides.map((s, i) => (
                <button
                  key={s.id || i}
                  onClick={() => setSlideIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === slideIdx ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/70"}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ═════════════════ Compra por categoría ═════════════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] text-[#002FA7] mb-1">Nuestro catálogo</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Compra por categoría</h2>
          </div>
          <button type="button" onClick={catalog.showAll} className="text-sm font-semibold text-[#002FA7] hover:underline" data-testid="cats-see-all">
            Ver todo →
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(data?.categories || []).map((c) => {
            const Icon = CATEGORY_ICONS[c.key] || Laptop;
            const disabled = c.count === 0;
            return (
              <button
                type="button"
                key={c.key}
                disabled={disabled}
                onClick={() => catalog.pickCategory(c.key)}
                className={`group aspect-square rounded-xl border p-4 flex flex-col items-center justify-center text-center transition-all ${
                  disabled
                    ? "border-zinc-100 bg-zinc-50 text-zinc-300 cursor-not-allowed"
                    : "border-zinc-200 bg-white hover:border-[#002FA7] hover:shadow-md hover:-translate-y-0.5"
                }`}
                data-testid={`cat-${c.key}`}
              >
                <Icon size={40} weight="duotone" className={disabled ? "text-zinc-300" : "text-[#002FA7] group-hover:scale-110 transition-transform"} />
                <div className="mt-3 font-heading font-bold text-sm">{c.label}</div>
                <div className={`text-[10px] uppercase tracking-widest ${disabled ? "text-zinc-300" : "text-zinc-500"} mt-0.5`}>
                  {c.count === 0 ? "Pronto" : `${c.count} producto${c.count === 1 ? "" : "s"}`}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Promo banner strip #1 — entre categorías y usos */}
      {promoBanners[0] && <PromoStrip banner={promoBanners[0]} index={0} />}

      {/* ═════════════════ ¿Para qué necesitas tu equipo? ═════════════════ */}
      <section className="mt-14 bg-gradient-to-br from-[#EEF3FF] to-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] text-[#002FA7]">Descubre tu equipo ideal</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">¿Para qué necesitas tu equipo?</h2>
            <p className="text-zinc-500 text-sm mt-2 max-w-xl mx-auto">Selecciona el uso principal y te mostramos los equipos que mejor se adaptan.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(data?.use_cases || []).map((uc) => {
              const Icon = USE_CASE_ICONS[uc.key] || Cpu;
              return (
                <button
                  type="button"
                  key={uc.key}
                  onClick={() => catalog.pickNeed(uc.key)}
                  className={`group bg-white rounded-xl border p-5 flex flex-col items-center text-center hover:border-[#002FA7] hover:shadow-md transition-all ${catalog.state.need === uc.key ? "border-[#002FA7] ring-1 ring-[#002FA7]/30" : "border-zinc-200"}`}
                  data-testid={`usecase-${uc.key}`}
                >
                  <div className="w-14 h-14 rounded-full bg-[#EEF3FF] flex items-center justify-center group-hover:bg-[#002FA7] transition-colors">
                    <Icon size={26} weight="duotone" className="text-[#002FA7] group-hover:text-white" />
                  </div>
                  <div className="mt-3 font-heading font-bold text-sm">{uc.label}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{uc.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════════════ Catálogo con filtros (mockup: "Ofertas y equipos destacados") ═════════════════ */}
      <HomeCatalog ctl={catalog} budgets={budgetList.length ? budgetList : undefined} needLabels={needLabels} wishlist={wishlist} onWish={toggleWish} onAdd={addToCart} onView={setQuick} />

      {/* ═════════════════ Ofertas destacadas (sólo con descuento real) ═════════════════ */}
      {data?.offers?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] text-[#E0000F] mb-1">Precios rebajados</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Ofertas destacadas</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => offersRef.current?.scrollBy({ left: -320, behavior: "smooth" })}
                className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                aria-label="Anterior"
              >
                <CaretLeft size={16} weight="bold" />
              </button>
              <button
                onClick={() => offersRef.current?.scrollBy({ left: 320, behavior: "smooth" })}
                className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                aria-label="Siguiente"
              >
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
          </div>
          <div ref={offersRef} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin">
            {data.offers.map((p) => (
              <HomeProductCard key={p.id} product={p} wished={wishlist.includes(p.id)} onWish={() => toggleWish(p.id)} onAdd={() => addToCart(p)} onView={() => setQuick(p)} />
            ))}
          </div>
        </section>
      )}

      {/* Promo banner strip #2 — entre ofertas y presupuesto */}
      {promoBanners[1] && <PromoStrip banner={promoBanners[1]} index={1} />}

      {/* ═════════════════ Compra por presupuesto ═════════════════ */}
      <section className="mt-14 bg-zinc-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6">
            <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] text-[#002FA7]">A tu medida</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">Compra por presupuesto</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {(data?.budgets || []).map((b) => (
              <button
                type="button"
                key={b.key}
                onClick={() => catalog.pickBudget(b.key)}
                className={`group bg-white border rounded-xl p-6 text-center hover:border-[#002FA7] hover:shadow-md transition-all ${catalog.state.budget === b.key ? "border-[#002FA7] ring-1 ring-[#002FA7]/30" : "border-zinc-200"}`}
                data-testid={`budget-${b.key}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">Rango</div>
                <div className="font-heading text-xl font-extrabold mt-1 group-hover:text-[#002FA7] transition-colors">
                  {b.label}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-zinc-500 group-hover:text-[#002FA7]">
                  Ver productos <CaretRight size={12} weight="bold" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════════ CTA WhatsApp ═════════════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 my-14">
        <div className="bg-zinc-900 text-white rounded-2xl p-8 sm:p-12 flex flex-wrap items-center gap-6 justify-between">
          <div className="flex items-center gap-5 flex-1 min-w-[280px]">
            <div className="hidden sm:flex w-14 h-14 rounded-full bg-[#25D366] items-center justify-center shrink-0">
              <WhatsappLogo size={30} weight="fill" />
            </div>
            <div>
              <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] text-emerald-400">Asesoría personalizada</div>
              <h3 className="font-heading text-xl sm:text-2xl font-extrabold mt-1">¿No sabes qué equipo elegir?</h3>
              <p className="text-sm text-zinc-300 mt-1">Cuéntanos qué uso le darás y te recomendamos la mejor opción por tu presupuesto.</p>
            </div>
          </div>
          {whatsappHref ? (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBA58] text-white font-bold px-6 py-3 rounded-full text-sm"
              data-testid="whatsapp-cta"
            >
              <WhatsappLogo size={16} weight="fill" /> Escríbenos por WhatsApp
            </a>
          ) : (
            <div className="text-xs text-zinc-400 italic">Agrega tu WhatsApp desde Ajustes de empresa</div>
          )}
        </div>
      </section>

      {/* Promo banner strip #3 — antes del trust bar */}
      {promoBanners[2] && <PromoStrip banner={promoBanners[2]} index={2} />}

      {/* ═════════════════ Trust bar ═════════════════ */}
      <section className="border-t border-zinc-100 bg-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, title: "Garantía extendida", subtitle: "Cobertura hasta 12 meses" },
            { icon: Truck,       title: "Envíos a todo México", subtitle: "En 24–72 horas hábiles" },
            { icon: CreditCard,  title: "Pagos seguros",       subtitle: "Meses sin intereses" },
            { icon: Headset,     title: "Soporte real",        subtitle: "Escríbenos cuando quieras" },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#EEF3FF] flex items-center justify-center shrink-0">
                <t.icon size={22} weight="duotone" className="text-[#002FA7]" />
              </div>
              <div>
                <div className="font-heading font-extrabold text-sm">{t.title}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{t.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═════════════════ Footer ═════════════════ */}
      <footer className="bg-zinc-900 text-zinc-300 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="font-heading font-extrabold text-white text-lg">{company.name}</div>
            <p className="text-xs text-zinc-500 mt-2">Todos los productos sujetos a disponibilidad.</p>
          </div>
          <div>
            <div className="font-heading font-bold text-white mb-2 text-xs uppercase tracking-widest">Tienda</div>
            <ul className="space-y-1.5 text-xs">
              <li><Link to="/catalogo" className="hover:text-white">Catálogo completo</Link></li>
              <li><Link to="/catalogo?offers=1" className="hover:text-white">Ofertas</Link></li>
              <li><Link to="/portal" className="hover:text-white">Mi cuenta</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold text-white mb-2 text-xs uppercase tracking-widest">Ayuda</div>
            <ul className="space-y-1.5 text-xs">
              <li>{whatsappHref
                ? <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:text-white">WhatsApp</a>
                : <span className="text-zinc-500">WhatsApp (configurar)</span>}</li>
              <li><Link to="/portal" className="hover:text-white">Seguimiento de pedido</Link></li>
              <li>Devoluciones</li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold text-white mb-2 text-xs uppercase tracking-widest">Compañía</div>
            <ul className="space-y-1.5 text-xs">
              <li>Términos</li>
              <li>Privacidad</li>
              {company.address && <li className="text-zinc-500">{company.address}</li>}
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8 text-center text-[11px] text-zinc-500">
          © {new Date().getFullYear()} {company.name}. Todos los derechos reservados.
        </div>
      </footer>
      <QuickViewModal product={quick} onClose={() => setQuick(null)} onAdd={addFromQuickView} companyName={company.name} whatsapp={whatsapp} />
      <CartDrawer />
    </div>
  );
}

/** CTA del hero — usa <Link> para rutas internas y <a> para URLs absolutas. */
function HeroCta({ href, label }) {
  const isExternal = /^https?:\/\//i.test(href);
  const className = "mt-6 inline-flex items-center gap-2 bg-white text-[#002FA7] font-bold text-sm px-6 py-3 rounded-full hover:bg-zinc-100 transition-colors";
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid="hero-cta">
        {label} <CaretRight size={14} weight="bold" />
      </a>
    );
  }
  return (
    <Link to={href} className={className} data-testid="hero-cta">
      {label} <CaretRight size={14} weight="bold" />
    </Link>
  );
}

/** Franja horizontal para banners promocionales entre secciones del home. */
function PromoStrip({ banner, index }) {
  const b = banner || {};
  const accent = b.accent_color || "#002FA7";
  const hasImage = !!b.image_url;
  const isExternal = /^https?:\/\//i.test(b.cta_href || "");
  const body = (
    <div
      className="relative overflow-hidden rounded-2xl h-[140px] sm:h-[180px] flex items-center px-6 sm:px-10 text-white group"
      style={{
        background: hasImage
          ? `linear-gradient(90deg, ${accent}E6 0%, ${accent}80 50%, transparent 100%), url(${b.image_url}) center/cover`
          : `linear-gradient(90deg, ${accent} 0%, #001a5c 100%)`,
      }}
    >
      <div className="relative z-10 max-w-xl">
        {b.title && <div className="font-heading text-lg sm:text-2xl font-extrabold leading-tight">{b.title}</div>}
        {b.subtitle && <p className="mt-1 text-xs sm:text-sm opacity-90 line-clamp-2">{b.subtitle}</p>}
        {b.cta_label && b.cta_href && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-white text-zinc-900 font-bold text-xs px-4 py-1.5 rounded-full group-hover:bg-zinc-100 transition-colors">
            {b.cta_label} <CaretRight size={11} weight="bold" />
          </div>
        )}
      </div>
    </div>
  );
  const wrapClass = "max-w-7xl mx-auto px-4 sm:px-6 mt-10 block";
  const testid = `promo-banner-${index}`;
  if (!b.cta_href) {
    return <div className={wrapClass} data-testid={testid}>{body}</div>;
  }
  if (isExternal) {
    return (
      <a href={b.cta_href} target="_blank" rel="noopener noreferrer" className={wrapClass} data-testid={testid}>
        {body}
      </a>
    );
  }
  return (
    <Link to={b.cta_href} className={wrapClass} data-testid={testid}>
      {body}
    </Link>
  );
}

